import React from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { CheckCircle2, ArrowRight, Settings, Ruler, Clock } from "lucide-react";
import PageBanner from "../components/PageBanner";
import { servicePages } from "../data/pageData";

const ServiceDetailPage = () => {
  const { slug } = useParams();
  const data = servicePages[slug];
  const [ref1, inView1] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [ref2, inView2] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [ref3, inView3] = useInView({ triggerOnce: true, threshold: 0.1 });

  if (!data) {
    return (
      <div className="inner-section" style={{ textAlign: 'center', padding: '120px 24px' }}>
        <h2>Service Not Found</h2>
        <p>The requested service page does not exist.</p>
        <Link to="/services" className="btn-primary" style={{ marginTop: 24, display: 'inline-flex' }}>View All Services</Link>
      </div>
    );
  }

  return (
    <>
      <PageBanner
        title={data.heading}
        subtitle={data.subheading}
        image={data.heroImage}
        breadcrumbs={[
          { label: "Services", link: "/services" },
          { label: data.heading }
        ]}
      />

      {/* Intro + Content */}
      <section className="inner-section" ref={ref1}>
        <div className="inner-container">
          <motion.div className="sd-intro" initial={{ opacity: 0, y: 30 }} animate={inView1 ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.6 }}>
            <p className="sd-intro-text">{data.intro}</p>
          </motion.div>
          <div className="sd-content-grid">
            <div className="sd-content-main">
              {data.sections.map((sec, i) => (
                <motion.div key={i} className="sd-section" initial={{ opacity: 0, y: 20 }}
                  animate={inView1 ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5, delay: 0.1 + i * 0.1 }}>
                  <h3>{sec.title}</h3>
                  {sec.content && <p>{sec.content}</p>}
                  {/* Applications Grid */}
                  {sec.title.includes('Applications') && data.applications && (
                    <div className="applications-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '12px', marginTop: '20px' }}>
                      {data.applications.map((app, idx) => (
                        <div key={idx} style={{ padding: '12px 16px', background: '#f5f5f5', borderRadius: '6px', fontSize: '14px', fontWeight: '500', textAlign: 'center' }}>
                          {app}
                        </div>
                      ))}
                    </div>
                  )}
                </motion.div>
              ))}
              
              {/* Challenging Structures Section */}
              {data.challengingStructures && (
                <motion.div className="sd-section" initial={{ opacity: 0, y: 20 }}
                  animate={inView1 ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5, delay: 0.4 }}>
                  <h3>Challenging Structures to Achieve</h3>
                  <div className="applications-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '12px', marginTop: '20px' }}>
                    {data.challengingStructures.map((struct, idx) => (
                      <div key={idx} style={{ padding: '10px 14px', background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: '6px', fontSize: '13px', fontWeight: '500', textAlign: 'center', color: '#9a3412' }}>
                        {struct}
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}
            </div>
            <div className="sd-sidebar">
              <div className="sd-sidebar-card">
                <h4><Settings size={20} /> Technical Specifications</h4>
                <ul className="sd-specs-list">
                  {data.specs.map((sp, i) => (
                    <li key={i}><span className="spec-label">{sp.label}</span><span className="spec-value">{sp.value}</span></li>
                  ))}
                </ul>
              </div>
              
              {/* Thumb Rules Card */}
              {data.thumbRules && (
                <div className="sd-sidebar-card" style={{ background: '#fef3c7', border: '1px solid #fde047' }}>
                  <h4 style={{ color: '#854d0e' }}><Ruler size={20} /> For Best Fit Thumb Rules for {slug.toUpperCase()}</h4>
                  <ul style={{ listStyle: 'none', padding: 0, margin: '12px 0 0 0' }}>
                    {data.thumbRules.map((rule, i) => (
                      <li key={i} style={{ padding: '8px 0', fontSize: '13px', color: '#713f12', borderBottom: i < data.thumbRules.length - 1 ? '1px solid #fde68a' : 'none' }}>
                        › {rule}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Materials */}
      <section className="inner-section bg-light" ref={ref2}>
        <div className="inner-container">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={inView2 ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.6 }}>
            <span className="section-subtitle">{slug === 'product-development' ? 'Our Capabilities' : 'Materials & Options'}</span>
            <h2 className="section-title">{slug === 'product-development' ? 'Development Capabilities' : 'Available Materials'}</h2>
            <div className="section-line" />
          </motion.div>
          <div className="materials-grid">
            {data.materials.map((mat, i) => (
              <motion.div key={i} className="material-card" initial={{ opacity: 0, y: 20 }}
                animate={inView2 ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.4, delay: i * 0.08 }}>
                <h4>{mat.name}</h4>
                <p>{mat.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cta-section">
        <div className="cta-inner">
          <h2 className="cta-title">Ready to Start Your {data.heading} Project?</h2>
          <p className="cta-desc">Upload your design or describe your requirements — our Bangalore team will provide a free quote within 2 hours.</p>
          <Link to="/contact" className="btn-primary">Get a Free Quote <ArrowRight size={18} /></Link>
        </div>
      </section>
    </>
  );
};

export default ServiceDetailPage;
